<script setup>
import { onMounted } from 'vue';
import { usePage } from '@inertiajs/vue3';

const page = usePage();

/**
 * Groove's loader verwacht dit stub-object op window plus init() met de
 * widget-id (het subdomein van de loader-URL). Zonder die aanroep tekent
 * de loader geen knop. De vorm volgt het officiële embed-script.
 */
function slice(list, start) {
    return Array.prototype.slice.call(list, start);
}

function ensureGrooveStub() {
    if (window.groove) {
        return;
    }

    const groove = {
        widget: null,
        loadedWidgets: {},
        classes: {
            Shim: null,
            Embeddable: function Embeddable() {
                this._beforeLoadCallQueue = [];
                this.shim = null;
                this.finalized = false;

                const exec = function exec(methodName) {
                    const args = slice(arguments, 1);

                    if (this.finalized) {
                        if (!this[methodName]) {
                            throw new TypeError(methodName + '() is not a valid widget method');
                        }

                        this[methodName].apply(this, args);

                        return;
                    }

                    this._beforeLoadCallQueue.push([methodName, args]);
                };

                this.initializeShim = function initializeShim() {
                    if (groove.classes.Shim) {
                        this.shim = new groove.classes.Shim(this);
                    }
                };
                this.exec = exec;
                this.init = function init() {
                    exec.apply(this, ['init'].concat(slice(arguments, 0)));
                    this.initializeShim();
                };
                this.onShimScriptLoad = this.initializeShim.bind(this);
                this.onload = undefined;
            },
        },
        scriptLoader: {
            callbacks: {},
            states: {},
            load(scriptId, src) {
                if (this.states[scriptId] === 'pending') {
                    return;
                }

                this.states[scriptId] = 'pending';

                const script = document.createElement('script');
                script.id = scriptId;
                script.type = 'text/javascript';
                script.async = true;
                script.defer = true;
                script.src = src;

                script.addEventListener('load', () => {
                    this.states[scriptId] = 'completed';
                    (this.callbacks[scriptId] || []).forEach((callback) => callback());
                }, false);

                const firstScript = document.getElementsByTagName('script')[0];
                firstScript.parentNode.insertBefore(script, firstScript);
            },
            addListener(scriptId, callback) {
                if (this.states[scriptId] === 'completed') {
                    callback();

                    return;
                }

                this.callbacks[scriptId] = this.callbacks[scriptId] || [];
                this.callbacks[scriptId].push(callback);
            },
        },
        createEmbeddable() {
            const embeddable = new groove.classes.Embeddable();

            if (!window.Proxy) {
                return embeddable;
            }

            return new Proxy(embeddable, {
                get(target, property) {
                    if (!(target instanceof groove.classes.Embeddable)) {
                        return target[property];
                    }

                    if (Object.prototype.hasOwnProperty.call(target, property) || property === 'onload') {
                        return target[property];
                    }

                    return function proxied() {
                        target.exec.apply(target, [property].concat(slice(arguments, 0)));
                    };
                },
            });
        },
        createWidget(loaderUrl) {
            const embeddable = groove.createEmbeddable();

            groove.scriptLoader.load('groove-script', loaderUrl);
            groove.scriptLoader.addListener('groove-iframe-shim-loader', embeddable.onShimScriptLoad);

            return embeddable;
        },
    };

    window.groove = groove;
}

onMounted(() => {
    const loaderUrl = page.props.grooveWidgetUrl;

    if (!loaderUrl || document.getElementById('groove-script')) {
        return;
    }

    const widgetId = new URL(loaderUrl).hostname.split('.')[0];

    ensureGrooveStub();
    window.groove.widget = window.groove.createWidget(loaderUrl);
    window.groove.widget.init(widgetId, {});
});
</script>

<template>
    <!-- De knop tekent Groove zelf, na het laden van de loader. -->
</template>
